import { refreshBrowserEdges } from "@wags/ui/browser-edges.tsx";
import { lockDocument } from "@wags/ui/lock-document.ts";
import {
    type Channels,
    decideRelease,
    type Gesture,
    flightFor,
    nextChannels,
    PIXEL_CHANNELS,
    type Release,
    type Scene,
    STILL,
    type Tap,
} from "./gesture-policy.ts";
import {
    centroid,
    containRect,
    fitView,
    hiddenInsets,
    type Insets,
    insetClip,
    panBounds,
    type Point,
    type Rect,
    spread,
    transformFor,
    uncoveredWindow,
    type View,
    viewOfRect,
} from "./pinch-geometry.ts";
import {
    CLOSE,
    createSpringDriver,
    SETTLE,
    type Spring,
} from "./spring-driver.ts";
import {
    createTouchRouter,
    createVelocitySampler,
    type TouchSequence,
} from "./touch-sequence.ts";

const PAGE_GAP = 20;
const HEADER_HEIGHT = 52;
const FALLBACK_ASPECT = 4 / 5;

export type ViewerMode = "inline" | "lifting" | "open" | "closing";

type ViewerSnapshot = { mode: ViewerMode; index: number };

type HeroConfig = {
    coveredBy: readonly { readonly current: Element | null }[];
    mediaIndexes: readonly number[];
    mediaIndex: number;
    onMediaIndexChange: (mediaIndex: number) => void;
};

export type PhotoViewer = ReturnType<typeof createPhotoViewer>;

function rectOf(el: Element): Rect {
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, w: r.width, h: r.height };
}

const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function createPhotoViewer() {
    let snapshot: ViewerSnapshot = { mode: "inline", index: 0 };
    const listeners = new Set<() => void>();

    let root: HTMLElement | null = null;
    let layer: HTMLElement | null = null;
    let backdrop: HTMLElement | null = null;
    let chrome: HTMLElement | null = null;
    let hero: HTMLElement | null = null;
    const frames = new Map<number, HTMLElement>();
    let config: HeroConfig = {
        coveredBy: [],
        mediaIndexes: [],
        mediaIndex: 0,
        onMediaIndexChange: () => {},
    };

    let area: Rect = { x: 0, y: 0, w: 1, h: 1 };
    const fits = new Map<number, Rect>();
    const crops = new Map<number, Insets>();
    let heroWindow: Rect = area;
    let inlineScale = 1;
    const m: Channels = {
        cx: 0,
        cy: 0,
        s: 1,
        page: 0,
        backdrop: 0,
        chrome: 0,
        crop: 0,
    };
    let gesture: Gesture = { kind: "none" };
    let leaving: { p: number; view: View } | null = null;
    const pointer = createVelocitySampler();
    let lastTap: Tap | null = null;
    let windowListening: AbortController | null = null;
    let unlockDocument: (() => void) | null = null;

    const emit = (next: Partial<ViewerSnapshot>) => {
        snapshot = { ...snapshot, ...next };
        for (const l of listeners) l();
    };

    const count = () => config.mediaIndexes.length;

    const aspectOf = (p: number) => {
        const img = frames.get(p)?.querySelector("img");
        return img && img.naturalWidth > 0 && img.naturalHeight > 0
            ? img.naturalWidth / img.naturalHeight
            : FALLBACK_ASPECT;
    };

    const fitOf = (p: number) => {
        const known = fits.get(p);
        if (known) return known;
        const fit = containRect(area, aspectOf(p));
        fits.set(p, fit);
        return fit;
    };

    const measureArea = (): Rect => {
        if (!layer) return area;
        const style = getComputedStyle(layer);
        const safeTop = Number.parseFloat(style.paddingTop) || 0;
        const safeBottom = Number.parseFloat(style.paddingBottom) || 0;
        const box = layer.getBoundingClientRect();
        const top = safeTop + HEADER_HEIGHT;
        return { x: 0, y: top, w: box.width, h: box.height - top - safeBottom };
    };

    const inlineRect = (p: number) =>
        containRect(hero ? rectOf(hero) : area, aspectOf(p));

    const cropOf = (p: number) => {
        const known = crops.get(p);
        if (known) return known;
        const crop = hiddenInsets(inlineRect(p), heroWindow);
        crops.set(p, crop);
        return crop;
    };

    const measure = () => {
        area = measureArea();
        heroWindow = hero
            ? uncoveredWindow(
                  rectOf(hero),
                  config.coveredBy.flatMap(({ current }) =>
                      current ? [rectOf(current)] : [],
                  ),
                  window.innerHeight,
              )
            : area;
        fits.clear();
        crops.clear();
    };

    const place = (el: HTMLElement, fit: Rect) => {
        el.style.left = `${fit.x}px`;
        el.style.top = `${fit.y}px`;
        el.style.width = `${fit.w}px`;
        el.style.height = `${fit.h}px`;
    };

    const shown = (p: number) => {
        const { mode, index } = snapshot;
        if (mode === "open") return Math.abs(p - index) <= 1;
        return mode !== "inline" && p === index;
    };

    const markHero = (active: boolean) =>
        hero?.toggleAttribute("data-photo-viewer-active", active);

    const render = () => {
        if (!root) return;
        const { index } = snapshot;
        if (m.page === 0) leaving = null;
        const shade = String(m.backdrop);
        if (backdrop && backdrop.style.opacity !== shade) {
            backdrop.style.opacity = shade;
            refreshBrowserEdges();
        }
        if (chrome) chrome.style.opacity = String(Math.max(0, m.chrome));
        for (const [p, el] of frames) {
            if (!shown(p)) {
                el.style.visibility = "hidden";
                continue;
            }
            const fit = fitOf(p);
            place(el, fit);
            el.style.visibility = "visible";
            el.style.clipPath =
                p === index && m.crop > 0.001
                    ? insetClip(cropOf(p), Math.min(1, m.crop))
                    : "";
            if (p === index) {
                el.style.transform = transformFor(
                    { cx: m.cx + m.page, cy: m.cy, s: m.s },
                    fit,
                );
                continue;
            }
            const home = leaving?.p === p ? leaving.view : fitView(fit);
            const offset = (p - index) * (area.w + PAGE_GAP) + m.page;
            el.style.transform = transformFor(
                { cx: home.cx + offset, cy: home.cy, s: home.s },
                fit,
            );
        }
    };

    const motion = createSpringDriver(
        m,
        PIXEL_CHANNELS,
        render,
        prefersReducedMotion,
    );
    const { animate } = motion;

    const releaseDocument = () => {
        unlockDocument?.();
        unlockDocument = null;
    };

    const isChrome = (target: EventTarget | null) =>
        target instanceof Element &&
        target.closest("[data-viewer-chrome]") !== null;

    const currentView = (): View => ({ cx: m.cx, cy: m.cy, s: m.s });

    const atFit = () => Math.abs(m.s - 1) < 0.02 && m.page === 0;

    const canPage = (direction: -1 | 1) => {
        const next = snapshot.index + direction;
        return next >= 0 && next < count();
    };

    const scene = (): Scene => ({
        lifting: snapshot.mode === "lifting",
        m,
        fit: fitOf(snapshot.index),
        area,
        inlineScale,
        canPrev: canPage(-1),
        canNext: canPage(1),
    });

    const beginGesture = (seq: TouchSequence) => {
        motion.stop();
        pointer.reset();
        const pts = touches.points();
        const first = pts[0];
        if (!first) {
            gesture = { kind: "none" };
            return;
        }
        const { mode } = snapshot;
        if (pts.length >= 2) {
            seq.pinch ??= {
                fromFit: mode === "open" && atFit(),
                fromLift: mode === "lifting",
            };
            gesture = {
                kind: "pinch",
                start: currentView(),
                origin: centroid(pts),
                spread: spread(pts),
            };
            return;
        }
        const b = panBounds(fitOf(snapshot.index), area, m.s);
        const pinched = seq.pinch !== null;
        gesture = {
            kind: "pan",
            start: currentView(),
            origin: first,
            axis:
                pinched || mode === "lifting" || m.s > 1.02
                    ? "free"
                    : m.page !== 0
                      ? "x"
                      : null,
            page: m.page,
            atMinX: !pinched && m.cx <= b.minX + 1,
            atMaxX: !pinched && m.cx >= b.maxX - 1,
        };
    };

    const enterOpen = () => {
        if (root) unlockDocument ??= lockDocument(root);
        emit({ mode: "open" });
    };

    const move = (seq: TouchSequence) => {
        const pts = touches.points();
        if (!pts[0]) return;
        const step = nextChannels(scene(), gesture, seq.pinch, pts);
        if (!step) return;
        if (gesture.kind === "pan") {
            gesture.axis = step.axis;
            seq.travel = Math.max(seq.travel, step.reach);
        }
        Object.assign(m, step.channels);
        if (step.commitOpen) enterOpen();
        pointer.sample(step.anchor);
        render();
    };

    const turnPage = (
        direction: -1 | 1,
        vx: number,
        drive: typeof animate = animate,
    ) => {
        leaving = { p: snapshot.index, view: currentView() };
        emit({ index: snapshot.index + direction });
        const mediaIndex = config.mediaIndexes[snapshot.index];
        if (mediaIndex !== undefined) config.onMediaIndexChange(mediaIndex);
        Object.assign(m, fitView(fitOf(snapshot.index)));
        m.page += direction * (area.w + PAGE_GAP);
        render();
        drive({ page: 0 }, SETTLE, { page: vx });
    };

    const detachWindow = () => {
        windowListening?.abort();
        windowListening = null;
        gesture = { kind: "none" };
        touches.reset();
    };

    const finishInline = () => {
        markHero(false);
        detachWindow();
        emit({ mode: "inline" });
        render();
        releaseDocument();
    };

    const returnInline = (
        spring: Spring,
        v: Point,
        drive: typeof animate = animate,
    ) => {
        const { index } = snapshot;
        const home = viewOfRect(inlineRect(index), fitOf(index));
        m.cx += m.page;
        m.page = 0;
        drive(
            {
                cx: home.cx,
                cy: home.cy,
                s: home.s,
                backdrop: 0,
                chrome: 0,
                crop: 1,
            },
            spring,
            { cx: v.x, cy: v.y },
            finishInline,
        );
    };

    const close = (v: Point) => {
        if (snapshot.mode !== "open") return;
        gesture = { kind: "none" };
        emit({ mode: "closing" });
        const mediaIndex = config.mediaIndexes[snapshot.index];
        if (mediaIndex !== undefined && mediaIndex !== config.mediaIndex) {
            config.onMediaIndexChange(mediaIndex);
        }
        returnInline(CLOSE, v, motion.go);
    };

    const dismiss = () => close(STILL);

    const release = (decision: Release) => {
        switch (decision.kind) {
            case "close":
                close(decision.velocity);
                return;
            case "page":
                turnPage(decision.direction, decision.vx);
                return;
            default: {
                const flight = flightFor(decision, scene());
                animate(flight.target, flight.spring, flight.velocity);
            }
        }
    };

    const releaseOpen = (
        ended: Gesture,
        seq: TouchSequence,
        lastPoint: Point | null,
    ) => {
        const now = performance.now();
        const tap =
            lastPoint && seq.travel < 10 && now - seq.startAt < 260
                ? lastPoint
                : null;
        const decision = decideRelease({
            gesture: ended,
            pinch: seq.pinch,
            s: m.s,
            page: m.page,
            cy: m.cy,
            velocity: pointer.velocity(),
            anchor: lastPoint ?? { x: m.cx, y: m.cy },
            tap,
            lastTap,
            now,
            width: area.w,
            canPrev: canPage(-1),
            canNext: canPage(1),
        });
        if (decision.kind === "zoomTo") lastTap = null;
        if (decision.kind === "settle" && decision.tap) {
            lastTap = { t: now, p: decision.tap };
        }
        release(decision);
    };

    const touches = createTouchRouter({
        claims: () => snapshot.mode === "lifting" || snapshot.mode === "open",
        ignores: isChrome,
        anchor: (pts) =>
            gesture.kind === "pinch" ? centroid(pts) : (pts[0] ?? null),
        begin: beginGesture,
        move,
        end: (seq, lastPoint) => {
            const ended = gesture;
            gesture = { kind: "none" };
            if (snapshot.mode === "lifting") returnInline(SETTLE, STILL);
            if (snapshot.mode === "open") releaseOpen(ended, seq, lastPoint);
        },
    });

    const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") touches.abandon();
    };

    const pageZoomed = () => (window.visualViewport?.scale ?? 1) > 1.01;

    const preventGesture = (e: Event) => {
        if (snapshot.mode !== "inline" || !pageZoomed()) e.preventDefault();
    };

    const onKey = (e: KeyboardEvent) => {
        if (snapshot.mode !== "open") return;
        if (e.key === "Escape") dismiss();
        if (e.key === "ArrowRight" && canPage(1)) {
            turnPage(1, 0, motion.go);
        }
        if (e.key === "ArrowLeft" && canPage(-1)) {
            turnPage(-1, 0, motion.go);
        }
    };

    const onResize = () => {
        if (snapshot.mode !== "open" || gesture.kind !== "none") return;
        motion.stop();
        measure();
        Object.assign(m, fitView(fitOf(snapshot.index)), { page: 0, crop: 0 });
        render();
    };

    const attachWindow = () => {
        windowListening?.abort();
        windowListening = new AbortController();
        const { signal } = windowListening;
        const capture = { passive: false, capture: true, signal };
        touches.listen(signal);
        window.addEventListener("gesturestart", preventGesture, capture);
        window.addEventListener("gesturechange", preventGesture, capture);
        window.addEventListener("keydown", onKey, { signal });
        window.addEventListener("resize", onResize, { signal });
        document.addEventListener("visibilitychange", onVisibilityChange, {
            signal,
        });
    };

    const beginLift = (p: number) => {
        motion.stop();
        measure();
        markHero(true);
        const start = viewOfRect(inlineRect(p), fitOf(p));
        inlineScale = start.s;
        Object.assign(m, start, { page: 0, backdrop: 0, chrome: 0, crop: 1 });
        emit({ mode: "lifting", index: p });
        attachWindow();
        render();
    };

    const heroPhoto = () => config.mediaIndexes.indexOf(config.mediaIndex);

    const onHeroTouchStart = (e: TouchEvent) => {
        const onHero = Array.from(e.touches).filter(
            (t) => t.target instanceof Node && hero?.contains(t.target),
        );
        if (snapshot.mode !== "inline" || onHero.length < 2 || pageZoomed())
            return;
        const p = heroPhoto();
        if (p < 0) return;
        e.preventDefault();
        beginLift(p);
        touches.track(onHero);
    };

    const openMedia = (mediaIndex: number) => {
        const p = config.mediaIndexes.indexOf(mediaIndex);
        if (snapshot.mode !== "inline" || p < 0) return;
        beginLift(p);
        enterOpen();
        const target = fitView(fitOf(p));
        motion.go(
            {
                cx: target.cx,
                cy: target.cy,
                s: 1,
                backdrop: 1,
                chrome: 1,
                crop: 0,
            },
            SETTLE,
            {},
        );
    };

    const refit = (p: number) => {
        fits.delete(p);
        crops.delete(p);
        const { mode, index } = snapshot;
        if (mode === "inline") return;
        if (p === index && mode === "open" && !motion.running() && atFit()) {
            Object.assign(m, fitView(fitOf(p)));
        }
        render();
    };

    const setConfig = (next: HeroConfig) => {
        config = next;
    };

    const attachHero = (el: HTMLElement | null) => {
        if (!el) return undefined;
        hero = el;
        const syncTouchAction = () => {
            el.style.touchAction = pageZoomed() ? "auto" : "pan-x pan-y";
        };
        syncTouchAction();
        const listening = new AbortController();
        const { signal } = listening;
        el.addEventListener("touchstart", onHeroTouchStart, {
            passive: false,
            signal,
        });
        el.addEventListener("gesturestart", preventGesture, {
            passive: false,
            signal,
        });
        window.visualViewport?.addEventListener("resize", syncTouchAction, {
            signal,
        });
        return () => {
            listening.abort();
            if (hero === el) hero = null;
        };
    };

    const mountFrame = (p: number) => (el: HTMLElement | null) => {
        if (!el) return undefined;
        frames.set(p, el);
        return () => {
            if (frames.get(p) === el) frames.delete(p);
        };
    };

    const mountChrome = (el: HTMLElement | null) => {
        chrome = el;
        el?.querySelector("button")?.focus({ preventScroll: true });
    };

    const mountRoot = (el: HTMLElement | null) => {
        if (!el) return undefined;
        root = el;
        layer = el.querySelector<HTMLElement>("[data-viewer-layer]");
        backdrop = el.querySelector<HTMLElement>("[data-viewer-backdrop]");
        return () => {
            motion.stop();
            detachWindow();
            releaseDocument();
            markHero(false);
            root = null;
            layer = null;
            backdrop = null;
            chrome = null;
            if (snapshot.mode !== "inline") emit({ mode: "inline" });
        };
    };

    return {
        attachHero,
        setConfig,
        mountRoot,
        mountFrame,
        mountChrome,
        openMedia,
        refit,
        close: dismiss,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot: () => snapshot,
    };
}
